import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {useAppSelector} from 'store/customer';
import {
    selectMaterial,
    selectPaths,
} from 'components/customer/BTM/store/btmSlice';
import {shallowEqual} from 'react-redux';
import styled from 'styled-components';
import {getSideName} from 'components/customer/BTM/helper/getSideByProfiles';
import {groupBy} from 'lodash';
import {useListEdgeProfilesQuery} from 'components/customer/BTM/store/btmApi';
import {BenchtopEdgeProfile} from 'components/customer/BTM/entity/BenchtopEdgeProfile';
import {OverlayTrigger} from 'shared/components/OverlayTrigger';
import {Path} from 'components/customer/BTM/entity/Path';
import {BenchtopMaterial} from 'components/customer/BTM/entity/BenchtopMaterial';
import {isMobile} from 'react-device-detect';

interface ProfileGroupInterface {
    profile: BenchtopEdgeProfile;
    paths: string[];
}

type Size = 'sm' | 'md';

interface OverviewProfilesProps {
    benchPaths: Path[];
    benchMaterial: BenchtopMaterial;
    className?: string;
    size?: Size;
}

export const OverviewProfiles = ({
    benchPaths,
    benchMaterial,
    className,
    size = 'md',
}: OverviewProfilesProps) => {
    const selectedPaths = useAppSelector(selectPaths, shallowEqual);
    const selectedMaterial = useAppSelector(selectMaterial, shallowEqual);

    const [paths, setPaths] = useState(benchPaths);
    const [material, setMaterial] = useState(benchMaterial);
    const [profileCap, setProfileCap] = useState(1);

    const {data: {data: edgeProfiles} = {data: [] as BenchtopEdgeProfile[]}} =
        useListEdgeProfilesQuery(
            {materialId: material && material.id},
            {skip: typeof material == 'undefined'}
        );

    const profiles = useMemo(() => {
        if (paths && edgeProfiles && edgeProfiles.length) {
            const profilesGroup = groupBy(
                paths.map((path, index) => ({...path, index})),
                'profile'
            );
            const profiles: ProfileGroupInterface[] = [];

            Object.keys(profilesGroup).forEach((key) => {
                if (key != 'undefined' && key != '0' && key != 'null') {
                    const profile = edgeProfiles.find(
                        (edgeProfile) => edgeProfile.id == Number(key)
                    );
                    const profilePaths = profilesGroup[String(key)];
                    let pathNames: string[];
                    if (profilePaths) {
                        pathNames = profilePaths.map(getSideName(paths));
                    }

                    profiles.push({
                        profile,
                        paths: pathNames,
                    });
                }
            });

            if (profiles.length > 0) return profiles;
        }

        return [];
    }, [edgeProfiles, paths]);

    const handleToggleProfileCap = useCallback(
        (event: React.MouseEvent) => {
            event.stopPropagation();

            if (profileCap == 1) {
                setProfileCap(99);
            } else {
                setProfileCap(1);
            }
        },
        [profileCap]
    );

    useEffect(() => {
        if (benchPaths == undefined) {
            setPaths(selectedPaths);
        }
    }, [benchPaths, selectedPaths]);

    useEffect(() => {
        if (benchMaterial == undefined) {
            setMaterial(selectedMaterial);
        }
    }, [benchMaterial, selectedMaterial]);

    return (
        <ProfileOverview
            className={className}
            $size={size}
            $hasMultiple={profiles.length > 1}>
            {profiles.map((profile, index) => {
                const edgeProfile = profile.profile;
                const profiledSides = profile.paths;

                if (size == 'sm' && index > profileCap) return null;

                return (
                    <Profile key={edgeProfile?.id} $size={size}>
                        <div>
                            {size == 'md' ||
                            edgeProfile?.image == undefined ||
                            edgeProfile?.image?.name == '' ? (
                                <div>
                                    {edgeProfile ? edgeProfile?.name : ''}
                                </div>
                            ) : null}
                            <ModifiedOverlay
                                $override={size == 'md'}
                                placement={size == 'md' ? 'left-start' : 'top'}
                                overlay={
                                    size == 'sm' ? (
                                        <div>{edgeProfile?.name}</div>
                                    ) : (
                                        <Overlay>
                                            <ImageContainer>
                                                <img
                                                    src={
                                                        edgeProfile &&
                                                        edgeProfile?.image?.name
                                                            ? edgeProfile?.image
                                                                  ?.name
                                                            : ''
                                                    }
                                                />
                                            </ImageContainer>
                                        </Overlay>
                                    )
                                }>
                                <img
                                    src={
                                        edgeProfile
                                            ? edgeProfile?.image?.name
                                            : ''
                                    }
                                />
                            </ModifiedOverlay>
                        </div>
                        {profiledSides.length > 0 ? (
                            <>
                                <strong>Edges: </strong>{' '}
                                {profiledSides.join(', ')} <br />
                            </>
                        ) : null}
                    </Profile>
                );
            })}

            {profiles.length > 2 && size == 'sm' ? (
                <MoreProfiles $size={size} onClick={handleToggleProfileCap}>
                    {profileCap == 1 ? 'show more' : 'show less'}
                </MoreProfiles>
            ) : null}
        </ProfileOverview>
    );
};

const ModifiedOverlay = styled(OverlayTrigger)<{$override: boolean}>`
    ${({$override}) => {
        if ($override) {
            return `> .tooltip-inner {
                padding: 0;
                background-color: transparent !important;
                border: 0 !important;
            }`;
        }
    }}
`;

const ImageContainer = styled.div`
    > img {
        width: 100%;
    }
`;

const ProfileOverview = styled.section<{$size: Size; $hasMultiple: boolean}>`
    ${({$size, $hasMultiple}) => {
        const columns = isMobile ? 1 : 2;

        if ($size == 'md') {
            return `
                display: flex;
                flex-wrap: wrap;
            `;
        } else {
            return `
                display: grid;
                grid-template-columns: repeat(${$hasMultiple ? columns : 1}, 1fr);
            `;
        }
    }}
`;

const Profile = styled.div<{$size: Size}>`
    font-size: 0.85em;
    margin: 15px 15px 0 4px;
    background: white;
    border-radius: 5px;
    box-shadow: 0px 1px 6px 1px #b1b1b1;
    padding: 10px;

    ${({$size}) => {
        if ($size == 'sm') {
            return `width: 100px`;
        }
    }};

    > div {
        display: ${({$size}) => ($size == 'md' ? 'flex' : 'initial')};
        align-items: center;
        margin-bottom: 10px;
        max-width: 150px;

        > div {
            width: ${({$size}) => ($size == 'md' ? '50%' : 'initial')};
            font-weight: ${({$size}) => ($size == 'md' ? 'initial' : '500')};
        }

        > img {
            padding-left: 10px;
            ${({$size}) => {
                if ($size == 'md') {
                    return `width: calc(50% - 10px)`;
                } else {
                    return `width: 100%`;
                }
            }}
        }
    }
`;

const MoreProfiles = styled(Profile)`
    font-size: 0.8em !important;
    font-weight: 500;
    color: ${({theme}) => theme.colors.primary.main};
    cursor: pointer;
    padding: 0;
    box-shadow: none;
`;

const Overlay = styled.div`
    z-index: 100;
    padding: 5px;
    background: white;
    border-radius: 8px;
    border: 1px solid rgb(var(--secondary_colour));

    > img {
        border-radius: 8px;
    }
`;
